import { useEffect, useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CreditCard, Calendar, Users, GraduationCap, CheckCircle, 
  AlertTriangle, Clock, Zap, Crown, Building2, ArrowUpCircle, Send, Receipt, Download
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MySubscriptionPage = () => {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [paymentConfig, setPaymentConfig] = useState(null);
  
  // Dialogs
  const [renewDialog, setRenewDialog] = useState(false);
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [processing, setProcessing] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  
  // Upgrade feature states
  const [availableUpgrades, setAvailableUpgrades] = useState([]);
  const [upgradeRequests, setUpgradeRequests] = useState([]);
  const [paymentType, setPaymentType] = useState('online');
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);
  const [selectedUpgradeCycle, setSelectedUpgradeCycle] = useState(null);

  useEffect(() => {
    if (user?.school_id) {
      fetchData();
    }
  }, [user?.school_id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes, configRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/school/${user?.school_id}`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/subscriptions/plans`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/payments/config`, { headers: getAuthHeader() }).catch(() => ({ data: { is_configured: false } })),
        axios.get(`${API_URL}/api/invoices`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      setSubscription(subRes.data);
      setPlans(plansRes.data || []);
      setPaymentConfig(configRes.data || { is_configured: false });
      // Store all invoices and filter pending
      const invoicesList = invoicesRes.data || [];
      setAllInvoices(Array.isArray(invoicesList) ? invoicesList : []);
      const pending = Array.isArray(invoicesList) ? invoicesList.filter(inv => inv.status === 'sent' || inv.status === 'overdue') : [];
      setPendingInvoices(pending);
      
      // Fetch available upgrades and requests
      try {
        const [upgradesRes, requestsRes] = await Promise.all([
          axios.get(`${API_URL}/api/subscriptions/available-upgrades`, { headers: getAuthHeader() }),
          axios.get(`${API_URL}/api/subscriptions/upgrade-requests`, { headers: getAuthHeader() })
        ]);
        setAvailableUpgrades(upgradesRes.data.available_upgrades || []);
        setUpgradeRequests(requestsRes.data || []);
      } catch (e) {
        console.log('Upgrade APIs not available');
      }
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = (plan, cycle) => {
    if (cycle === 'monthly') return plan.price_monthly;
    if (cycle === 'quarterly') return plan.price_monthly * 3 * 0.9;
    return plan.price_yearly;
  };

  const getDaysLeft = () => {
    if (!subscription?.end_date) return 0;
    const endDate = new Date(subscription.end_date);
    const today = new Date();
    return Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const initiatePayment = async (planId, cycle) => {
    if (!paymentConfig?.is_configured) {
      toast.error('Payment gateway is not configured. Please contact support.');
      return;
    }

    setProcessing(true);
    try {
      // Create order
      const orderRes = await axios.post(
        `${API_URL}/api/subscriptions/payment/create-order/${user?.school_id}?plan_id=${planId}&billing_cycle=${cycle}`,
        {},
        { headers: getAuthHeader() }
      );

      const orderData = orderRes.data;

      // Load Razorpay
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error('Failed to load payment gateway');
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: paymentConfig.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Cloud Campus Schools',
        description: `${orderData.plan_name} - ${cycle} subscription`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API_URL}/api/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, { headers: getAuthHeader() });

            toast.success('Payment successful! Your subscription has been renewed.');
            setRenewDialog(false);
            setUpgradeDialog(false);
            fetchData();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: orderData.school_name,
          email: user?.email
        },
        theme: {
          color: '#3B82F6'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  // Download/Print Invoice PDF
  const handleDownloadInvoice = async (invoiceId, invoiceNumber) => {
    const filename = `Invoice_${invoiceNumber || invoiceId}.pdf`;
    
    try {
      toast.info('Downloading invoice...');
      
      const response = await axios.get(
        `${API_URL}/api/invoices/${invoiceId}/pdf`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      if (!response.data || response.data.size === 0) {
        toast.error('Empty PDF response');
        return;
      }
      
      // Use file-saver for reliable download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      saveAs(blob, filename);
      
      toast.success('Invoice downloaded! Open to print.');
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download invoice');
    }
  };

  const payInvoiceOnline = async (invoice) => {
    if (!paymentConfig?.is_configured) {
      toast.error('Payment gateway is not configured. Please contact support.');
      return;
    }

    setProcessing(true);
    try {
      // Create Razorpay order for invoice
      const orderRes = await axios.post(
        `${API_URL}/api/invoices/${invoice.id}/create-payment-order`,
        {},
        { headers: getAuthHeader() }
      );

      const orderData = orderRes.data;

      // Load Razorpay
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error('Failed to load payment gateway');
        return;
      }

      // Open Razorpay checkout
      const options = {
        key: paymentConfig.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Cloud Campus Schools',
        description: `Invoice Payment - ${invoice.invoice_number}`,
        order_id: orderData.order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API_URL}/api/invoices/${invoice.id}/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, { headers: getAuthHeader() });

            toast.success('Payment successful! Invoice has been marked as paid.');
            fetchData();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          email: user?.email
        },
        theme: {
          color: '#3B82F6'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  // Handle Upgrade Request
  const handleUpgradeRequest = async () => {
    if (!selectedUpgradePlan || !selectedUpgradeCycle) {
      toast.error('Please select a plan and billing cycle');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/subscriptions/upgrade-request`,
        {
          new_plan_id: selectedUpgradePlan.plan.id,
          new_billing_cycle: selectedUpgradeCycle.cycle,
          payment_type: paymentType
        },
        { headers: getAuthHeader() }
      );

      const result = response.data;

      if (result.status === 'payment_required' && result.payment) {
        // Online payment - open Razorpay
        const loaded = await loadRazorpay();
        if (!loaded) {
          toast.error('Failed to load payment gateway');
          setProcessing(false);
          return;
        }

        const options = {
          key: result.payment.key_id,
          amount: result.payment.amount,
          currency: result.payment.currency,
          name: 'Cloud Campus Schools',
          description: `Upgrade to ${selectedUpgradePlan.plan.name} - ${selectedUpgradeCycle.cycle}`,
          order_id: result.payment.order_id,
          handler: async (paymentResponse) => {
            try {
              // Verify payment
              await axios.post(`${API_URL}/api/invoices/${result.invoice.id}/verify-payment`, {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              }, { headers: getAuthHeader() });

              toast.success('Upgrade successful! Your subscription has been upgraded.');
              setUpgradeDialog(false);
              fetchData();
            } catch (error) {
              toast.error('Payment verification failed');
            }
          },
          prefill: {
            email: user?.email
          },
          theme: {
            color: '#3B82F6'
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else if (result.status === 'request_submitted') {
        // Offline payment request submitted
        toast.success('Upgrade request submitted! Super Admin will review and approve it.');
        setUpgradeDialog(false);
        fetchData();
      } else if (result.status === 'invoice_created') {
        // Invoice created but payment gateway not available
        toast.info(result.message || 'Invoice created. Please contact admin for payment.');
        setUpgradeDialog(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit upgrade request');
    } finally {
      setProcessing(false);
    }
  };

  const openRenewDialog = () => {
    setSelectedPlan(subscription?.plan);
    setBillingCycle(subscription?.billing_cycle || 'monthly');
    setRenewDialog(true);
  };

  const openUpgradeDialog = () => {
    setSelectedUpgradePlan(null);
    setSelectedUpgradeCycle(null);
    setPaymentType('online');
    setUpgradeDialog(true);
  };

  const daysLeft = getDaysLeft();
  const isExpiringSoon = daysLeft <= 7 && daysLeft > 0;
  const isExpired = daysLeft <= 0;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return <Badge className="bg-green-500">Active</Badge>;
      case 'trial': return <Badge className="bg-blue-500">Trial</Badge>;
      case 'expired': return <Badge variant="destructive">Expired</Badge>;
      case 'cancelled': return <Badge variant="secondary">Cancelled</Badge>;
      case 'pending': return <Badge variant="outline" className="bg-yellow-100">Pending</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'basic': return <Building2 className="h-6 w-6" />;
      case 'standard': return <Zap className="h-6 w-6" />;
      case 'premium': return <Crown className="h-6 w-6" />;
      default: return <Building2 className="h-6 w-6" />;
    }
  };

  const getRequestStatusBadge = (status) => {
    switch (status) {
      case 'pending': return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Pending Approval</Badge>;
      case 'approved': return <Badge className="bg-blue-500">Approved - Awaiting Payment</Badge>;
      case 'completed': return <Badge className="bg-green-500">Completed</Badge>;
      case 'rejected': return <Badge variant="destructive">Rejected</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="My Subscription">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  const hasPendingUpgradeRequest = upgradeRequests.some(r => r.status === 'pending' || r.status === 'approved');

  return (
    <DashboardLayout title="My Subscription">
      <div className="space-y-6">
        {/* Pending Invoices Alert */}
        {pendingInvoices.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-orange-500 bg-orange-50 dark:bg-orange-900/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-800 dark:text-orange-400">
                  <AlertTriangle className="h-5 w-5" />
                  Pending Payment{pendingInvoices.length > 1 ? 's' : ''}
                </CardTitle>
                <CardDescription className="text-orange-700 dark:text-orange-500">
                  You have {pendingInvoices.length} pending invoice{pendingInvoices.length > 1 ? 's' : ''} requiring payment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingInvoices.map((invoice) => (
                    <div 
                      key={invoice.id} 
                      className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border"
                    >
                      <div>
                        <p className="font-semibold">{invoice.invoice_number}</p>
                        <p className="text-sm text-muted-foreground">
                          {invoice.invoice_type === 'renewal' ? 'Renewal' : invoice.invoice_type === 'upgrade' ? 'Upgrade' : 'Subscription'} • Due: {invoice.due_date}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-lg">₹{(invoice.total_amount || invoice.amount)?.toLocaleString()}</p>
                        </div>
                        {paymentConfig?.is_configured ? (
                          <Button 
                            onClick={() => payInvoiceOnline(invoice)}
                            disabled={processing}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="pay-invoice-btn"
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Pay Now
                          </Button>
                        ) : (
                          <Badge variant="outline">Contact Admin</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Pending Upgrade Requests */}
        {upgradeRequests.filter(r => r.status === 'pending' || r.status === 'approved').length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-blue-500 bg-blue-50 dark:bg-blue-900/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
                  <ArrowUpCircle className="h-5 w-5" />
                  Pending Upgrade Request
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upgradeRequests.filter(r => r.status === 'pending' || r.status === 'approved').map((req) => (
                  <div key={req.id} className="p-4 bg-white dark:bg-gray-800 rounded-lg border">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold">{req.current_plan_name} → {req.new_plan_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {req.current_billing_cycle} → {req.new_billing_cycle} • Amount: ₹{req.amount?.toLocaleString()}
                        </p>
                      </div>
                      {getRequestStatusBadge(req.status)}
                    </div>
                    {req.status === 'approved' && req.invoice_id && (
                      <p className="mt-2 text-sm text-blue-600">Invoice generated. Please pay to complete the upgrade.</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Current Subscription */}
        {subscription && subscription.status !== 'no_subscription' ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className={isExpired ? 'border-red-500' : isExpiringSoon ? 'border-amber-500' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="h-5 w-5" />
                      Current Subscription
                    </CardTitle>
                    <CardDescription>Your subscription details and usage</CardDescription>
                  </div>
                  {getStatusBadge(subscription.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Warning banners */}
                {isExpired && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-400">Subscription Expired</p>
                      <p className="text-sm text-red-700 dark:text-red-500">Your subscription has expired. Please renew to continue using all features.</p>
                    </div>
                  </div>
                )}
                {isExpiringSoon && !isExpired && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
                    <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-400">Expiring Soon</p>
                      <p className="text-sm text-amber-700 dark:text-amber-500">Your subscription expires in {daysLeft} days. Renew now to avoid service interruption.</p>
                    </div>
                  </div>
                )}

                {/* Plan details */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
                      <div className="p-2 bg-primary/10 rounded-lg">
                        {getPlanIcon(subscription.plan?.plan_type)}
                      </div>
                      <div>
                        <p className="font-semibold text-lg">{subscription.plan?.name || 'Unknown Plan'}</p>
                        <p className="text-sm text-muted-foreground capitalize">{subscription.billing_cycle} billing</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground">Start Date</p>
                        <p className="font-medium">{subscription.start_date ? new Date(subscription.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <p className="text-xs text-muted-foreground">End Date</p>
                        <p className="font-medium">{subscription.end_date ? new Date(subscription.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</p>
                      </div>
                    </div>

                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground mb-1">Days Remaining</p>
                      <div className="flex items-center gap-2">
                        <Progress value={Math.max(0, (daysLeft / 30) * 100)} className="flex-1" />
                        <span className="font-semibold">{Math.max(0, daysLeft)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Usage */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Resource Usage</h4>
                    
                    <div className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-blue-500" />
                          <span className="text-sm">Students</span>
                        </div>
                        <span className="text-sm font-medium">
                          {subscription.usage?.students || 0} / {subscription.usage?.student_limit || '∞'}
                        </span>
                      </div>
                      <Progress 
                        value={subscription.usage?.student_limit ? 
                          (subscription.usage.students / subscription.usage.student_limit) * 100 : 0} 
                      />
                    </div>

                    <div className="p-3 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <GraduationCap className="h-4 w-4 text-green-500" />
                          <span className="text-sm">Teachers</span>
                        </div>
                        <span className="text-sm font-medium">
                          {subscription.usage?.teachers || 0} / {subscription.usage?.teacher_limit || '∞'}
                        </span>
                      </div>
                      <Progress 
                        value={subscription.usage?.teacher_limit ? 
                          (subscription.usage.teachers / subscription.usage.teacher_limit) * 100 : 0} 
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex gap-3 flex-wrap">
                <Button onClick={openRenewDialog} disabled={!paymentConfig?.is_configured} data-testid="renew-btn">
                  <CreditCard className="h-4 w-4 mr-2" /> Renew Subscription
                </Button>
                {availableUpgrades.length > 0 && !hasPendingUpgradeRequest && (
                  <Button variant="outline" onClick={openUpgradeDialog} data-testid="upgrade-btn">
                    <ArrowUpCircle className="h-4 w-4 mr-2" /> Upgrade Plan
                  </Button>
                )}
                {!paymentConfig?.is_configured && (
                  <p className="text-sm text-muted-foreground">Payment gateway not configured</p>
                )}
              </CardFooter>
            </Card>
          </motion.div>
        ) : (
          <Card className="border-amber-500">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Active Subscription</h3>
              <p className="text-muted-foreground">Please contact Super Admin to activate your subscription.</p>
            </CardContent>
          </Card>
        )}

        {/* Invoice History */}
        {allInvoices.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoice History
              </CardTitle>
              <CardDescription>All your subscription invoices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Invoice #</th>
                      <th className="text-left py-3 px-4 font-medium">Date</th>
                      <th className="text-left py-3 px-4 font-medium">Amount</th>
                      <th className="text-left py-3 px-4 font-medium">Status</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 font-medium">{invoice.invoice_number}</td>
                        <td className="py-3 px-4">{invoice.created_at?.split('T')[0] || invoice.due_date}</td>
                        <td className="py-3 px-4">
                          <span className="font-semibold">₹{(invoice.total_amount || invoice.amount || 0).toLocaleString()}</span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={
                            invoice.status === 'paid' ? 'default' :
                            invoice.status === 'overdue' ? 'destructive' :
                            'secondary'
                          } className={
                            invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                            invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }>
                            {invoice.status === 'paid' ? 'Paid' : 
                             invoice.status === 'overdue' ? 'Overdue' : 
                             invoice.status === 'sent' ? 'Pending' : invoice.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDownloadInvoice(invoice.id, invoice.invoice_number)}
                              title="Download/Print Invoice"
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Print
                            </Button>
                            {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                              <Button 
                                size="sm" 
                                onClick={() => payInvoiceOnline(invoice)}
                                disabled={processing}
                              >
                                <CreditCard className="h-4 w-4 mr-1" />
                                Pay Now
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Available Plans */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Available Plans</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`relative ${subscription?.plan_id === plan.id ? 'border-primary border-2' : ''}`}>
                  {subscription?.plan_id === plan.id && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary">Current Plan</Badge>
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <div className={`mx-auto p-3 rounded-full ${
                      plan.plan_type === 'premium' ? 'bg-amber-100 text-amber-600' :
                      plan.plan_type === 'standard' ? 'bg-blue-100 text-blue-600' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {getPlanIcon(plan.plan_type)}
                    </div>
                    <CardTitle>{plan.name}</CardTitle>
                    <div className="text-3xl font-bold">
                      ₹{plan.price_monthly.toLocaleString()}
                      <span className="text-sm font-normal text-muted-foreground">/month</span>
                    </div>
                    {plan.price_yearly && (
                      <p className="text-sm text-green-600">
                        Save ₹{(plan.price_monthly * 12 - plan.price_yearly).toLocaleString()} yearly
                      </p>
                    )}
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Up to {plan.max_students.toLocaleString()} students
                      </li>
                      <li className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Up to {plan.max_teachers.toLocaleString()} teachers
                      </li>
                      {plan.features?.slice(0, 4).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Renew Dialog */}
      <Dialog open={renewDialog} onOpenChange={setRenewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Renew Subscription</DialogTitle>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg flex items-center gap-3">
                {getPlanIcon(selectedPlan.plan_type)}
                <div>
                  <p className="font-semibold">{selectedPlan.name}</p>
                  <p className="text-sm text-muted-foreground">Current plan</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium">Billing Cycle</label>
                <Select value={billingCycle} onValueChange={setBillingCycle}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly - ₹{selectedPlan.price_monthly?.toLocaleString()}</SelectItem>
                    <SelectItem value="quarterly">Quarterly - ₹{(selectedPlan.price_monthly * 3 * 0.9).toLocaleString()} (10% off)</SelectItem>
                    <SelectItem value="yearly">Yearly - ₹{selectedPlan.price_yearly?.toLocaleString()} (Best value)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-4 border rounded-lg">
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>₹{calculatePrice(selectedPlan, billingCycle).toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewDialog(false)}>Cancel</Button>
            <Button 
              onClick={() => initiatePayment(selectedPlan?.id, billingCycle)} 
              disabled={processing}
              data-testid="pay-now-btn"
            >
              {processing ? 'Processing...' : 'Pay Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog */}
      <Dialog open={upgradeDialog} onOpenChange={setUpgradeDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpCircle className="h-5 w-5" />
              Upgrade Subscription
            </DialogTitle>
            <DialogDescription>
              Choose a higher plan or longer billing cycle. Downgrades are not allowed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Plan Info */}
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">Current Plan</p>
              <p className="font-semibold">{subscription?.plan?.name} • {subscription?.billing_cycle}</p>
            </div>

            {/* Select Plan */}
            <div>
              <label className="text-sm font-medium">Select New Plan</label>
              <div className="mt-2 space-y-2">
                {availableUpgrades.map((upgrade) => (
                  <div
                    key={upgrade.plan.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedUpgradePlan?.plan.id === upgrade.plan.id 
                        ? 'border-primary bg-primary/5' 
                        : 'hover:bg-muted'
                    }`}
                    onClick={() => {
                      setSelectedUpgradePlan(upgrade);
                      setSelectedUpgradeCycle(upgrade.available_cycles[0]);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getPlanIcon(upgrade.plan.plan_type)}
                        <div>
                          <p className="font-medium">{upgrade.plan.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {upgrade.plan.max_students} students • {upgrade.plan.max_teachers} teachers
                          </p>
                        </div>
                      </div>
                      <Badge variant={upgrade.plan.plan_type === 'premium' ? 'default' : 'secondary'}>
                        {upgrade.plan.plan_type}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Select Billing Cycle */}
            {selectedUpgradePlan && (
              <div>
                <label className="text-sm font-medium">Billing Cycle</label>
                <Select 
                  value={selectedUpgradeCycle?.cycle || ''} 
                  onValueChange={(val) => {
                    const cycle = selectedUpgradePlan.available_cycles.find(c => c.cycle === val);
                    setSelectedUpgradeCycle(cycle);
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select billing cycle" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedUpgradePlan.available_cycles.map((cycle) => (
                      <SelectItem key={cycle.cycle} value={cycle.cycle}>
                        {cycle.label} - ₹{cycle.price?.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Payment Type */}
            {selectedUpgradeCycle && (
              <div>
                <label className="text-sm font-medium">Payment Method</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div
                    className={`p-3 border rounded-lg cursor-pointer text-center ${
                      paymentType === 'online' ? 'border-primary bg-primary/5' : 'hover:bg-muted'
                    } ${!paymentConfig?.is_configured ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => paymentConfig?.is_configured && setPaymentType('online')}
                  >
                    <CreditCard className="h-5 w-5 mx-auto mb-1" />
                    <p className="text-sm font-medium">Pay Online</p>
                    <p className="text-xs text-muted-foreground">Instant activation</p>
                  </div>
                  <div
                    className={`p-3 border rounded-lg cursor-pointer text-center ${
                      paymentType === 'offline' ? 'border-primary bg-primary/5' : 'hover:bg-muted'
                    }`}
                    onClick={() => setPaymentType('offline')}
                  >
                    <Send className="h-5 w-5 mx-auto mb-1" />
                    <p className="text-sm font-medium">Request Offline</p>
                    <p className="text-xs text-muted-foreground">Admin approval needed</p>
                  </div>
                </div>
              </div>
            )}

            {/* Price Summary */}
            {selectedUpgradeCycle && (
              <div className="p-4 border rounded-lg bg-green-50 dark:bg-green-900/20">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">Total Amount</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedUpgradePlan?.plan.name} • {selectedUpgradeCycle?.cycle}
                    </p>
                  </div>
                  <p className="text-2xl font-bold text-green-600">
                    ₹{selectedUpgradeCycle?.price?.toLocaleString()}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleUpgradeRequest} 
              disabled={processing || !selectedUpgradePlan || !selectedUpgradeCycle}
              data-testid="confirm-upgrade-btn"
            >
              {processing ? 'Processing...' : paymentType === 'online' ? 'Upgrade & Pay' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default MySubscriptionPage;
